import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CompletedProjectPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [activeTab, setActiveTab] = useState("HOME");
  const [selectedImage, setSelectedImage] = useState(null);

  const toAbsUrl = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    const base = (API_BASE_URL || "").replace("/api", "");
    return `${base}${url.startsWith("/") ? url : `/${url}`}`;
  };

  const fetchProjectsList = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/projects/completed-cards`);
      const list = Array.isArray(res.data) ? res.data : [];
      setProjects(list);
      if (list.length > 0 && !projectId) setCurrentProject(list[0]);
    } catch (error) {
      console.error("Error fetching completed projects:", error);
    }
  }, [projectId]);

  const fetchProjectById = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/get-project/${id}`);
      setCurrentProject(res.data);
    } catch (error) {
      console.error("Error fetching completed project by id:", error);
    }
  }, []);

  const fetchProjectGallery = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/projects/admin/gallery/${id}`);
      if (Array.isArray(res.data) && res.data.length > 0) {
        const gallery = res.data[0];
        const parsedImages = Array.isArray(gallery.gallery_data)
          ? gallery.gallery_data
          : JSON.parse(gallery.gallery_data || "[]");

        const sortedImages = parsedImages.sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );

        setCurrentProject((prev) => ({
          ...prev,
          galleryImages: sortedImages,
        }));
      } else {
        setCurrentProject((prev) => ({ ...prev, galleryImages: [] }));
      }
    } catch (error) {
      console.error("❌ Error fetching project gallery:", error);
      setCurrentProject((prev) => ({ ...prev, galleryImages: [] }));
    }
  }, []);

  useEffect(() => {
    fetchProjectsList();
  }, [fetchProjectsList]);

  useEffect(() => {
    if (projectId) {
      fetchProjectById(projectId);
      fetchProjectGallery(projectId);
    } else if (projects.length > 0 && !currentProject) {
      setCurrentProject(projects[0]);
    }
  }, [projectId, projects, currentProject, fetchProjectById, fetchProjectGallery]);

  const handleProjectChange = (e) => {
    const newId = e.target.value;
    navigate(`/completed-projects/${newId}`);
  };

  const handleBackToProjects = () => navigate("/projects");
  const openLightbox = (image) => setSelectedImage(image);
  const closeLightbox = () => setSelectedImage(null);

  if (projects.length === 0)
    return (
      <div className="min-h-screen flex justify-center items-center px-4 text-center">
        <p className="text-gray-500 text-base sm:text-lg">
          Fetching completed projects...
        </p>
      </div>
    );

  if (!currentProject)
    return (
      <div className="min-h-screen flex justify-center items-center px-4 text-center">
        <p className="text-gray-500 text-base sm:text-lg">
          Loading project details...
        </p>
      </div>
    );

  const title = currentProject.project_title || currentProject.project_name || "Project";
  const status = currentProject.status || currentProject.card_status || "Completed";
  const description = currentProject.description || currentProject.card_description || "";
  const rera = currentProject.rera || currentProject.card_rera || "";
  const extent = currentProject.extent || currentProject.card_extent || "";
  const location =
    currentProject.location ||
    currentProject.card_location ||
    currentProject.project_badge_location ||
    "";
  const mainImage = toAbsUrl(currentProject.image || currentProject.card_image || null);
  const brochureUrl = currentProject.brochure || null;

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Breadcrumbs & Selector */}
      <div className="bg-gray-50 py-4 sm:py-6 px-3 sm:px-4 border-b">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs sm:text-sm md:text-base">
              <button
                onClick={handleBackToProjects}
                className="text-blue-600 hover:text-blue-700 font-semibold"
              >
                Completed Projects
              </button>
              <span className="text-gray-400">/</span>
              <span className="text-gray-700 font-medium truncate max-w-[200px] sm:max-w-none">
                {title}
              </span>
            </div>

            {/* Selector and Back Button */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full sm:w-auto sm:flex-wrap justify-end">
              <select
                value={currentProject.project_id || ""}
                onChange={handleProjectChange}
                className="px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 outline-none bg-white text-gray-700 text-xs sm:text-sm md:text-base font-medium w-full sm:w-auto max-w-[240px]"
              >
                {projects.map((p) => (
                  <option key={p.project_id} value={p.project_id}>
                    {p.project_name || p.project_title || "Untitled"}
                  </option>
                ))}
              </select>

              <button
                onClick={handleBackToProjects}
                className="bg-green-600 text-white px-4 sm:px-6 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors w-full sm:w-auto max-w-[180px] text-xs sm:text-sm md:text-base text-center"
              >
                Back to Projects
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b sticky top-14 sm:top-16 z-40 overflow-x-auto">
        <div className="container mx-auto max-w-7xl px-2 sm:px-4">
          <nav className="flex gap-2 sm:gap-3 overflow-x-auto text-xs sm:text-sm">
            {["HOME", "GALLERY"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 sm:px-6 py-2 sm:py-3 font-semibold rounded-lg transition-colors whitespace-nowrap ${
                  activeTab === tab
                    ? "bg-green-600 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="py-8 sm:py-10 md:py-12 px-3 sm:px-4 md:px-6">
        <div className="container mx-auto max-w-7xl">
          {activeTab === "GALLERY" && (
            <div>
              <div className="mb-6 sm:mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
                  Project Gallery
                </h2>
                <p className="text-gray-600 text-sm sm:text-base">
                  Explore our project through these images
                </p>
              </div>

              {currentProject.galleryImages &&
              currentProject.galleryImages.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {currentProject.galleryImages.map((item, index) => (
                    <div
                      key={index}
                      className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer bg-gradient-to-br from-gray-50 to-gray-100"
                      onClick={() => openLightbox(item.image)}
                    >
                      <div className="aspect-[4/3] sm:h-64 flex items-center justify-center p-3 sm:p-4">
                        <img
                          src={toAbsUrl(item.image)}
                          alt={`Gallery Image ${index + 1}`}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>

                      <div className="p-3 sm:p-4 bg-white text-center sm:text-left">
                        <p className="text-gray-800 font-medium text-xs sm:text-sm">
                          {item.date
                            ? new Date(item.date).toLocaleDateString()
                            : ""}
                        </p>
                        <p className="text-gray-600 text-xs sm:text-sm mt-1">
                          {item.description || ""}
                        </p>
                      </div>

                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                        <svg
                          className="w-8 sm:w-12 h-8 sm:h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                          />
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center text-sm sm:text-lg mt-4">
                  No gallery images available for this project.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-3 sm:top-4 right-3 sm:right-4 text-white hover:text-gray-300"
            onClick={closeLightbox}
          >
            <svg
              className="w-8 sm:w-10 h-8 sm:h-10"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <img
            src={toAbsUrl(selectedImage)}
            alt="Gallery fullscreen"
            className="max-w-full max-h-[80vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CompletedProjectPage;
